import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import moment from "moment";
import { useDispatch } from "react-redux";
import { cancelContainerMoveOut } from "../_redux/order/orderCrud";
import {
  fetchMyOrderDetailCard,
  fetchMyOrderTimeline,
} from "../_redux/order/orderAction";

/**
 * Whether a container order's booked move-out can still be cancelled: there is
 * one, the order is live, the move-out has not been completed, and the
 * move-out day has not ended. The server makes the final decision.
 *
 * @param {Object} orderDetail
 * @returns {boolean}
 */
const canCancelMoveOut = (orderDetail) =>
  Boolean(orderDetail?.return_date) &&
  // is_cancel is 1 on a cancelled order and null otherwise; compared as a
  // number so a "0" from the API never reads as cancelled.
  Number(orderDetail?.is_cancel) !== 1 &&
  String(orderDetail?.container_fulfillment_stage) !== "4" &&
  String(orderDetail?.container_fulfillment_stage) !== "5" &&
  moment().isSameOrBefore(moment(orderDetail.return_date).endOf("day"));

/**
 * Stripe's reason for refusing to resume billing, which the server sends with
 * its 409 as `data.detail`. Empty when there is none or it is not text.
 *
 * @param {Object} err The rejected request's error.
 * @returns {string}
 */
const refusalDetail = (err) => {
  const detail = err?.response?.data?.data?.detail;
  return typeof detail === "string" ? detail.trim() : "";
};

/**
 * "Cancel move-out" on a container order: asks for confirmation, then cancels
 * the move-out so the order carries on and billing is resumed.
 *
 * @param {Object} props
 * @param {Object} props.orderDetail The container order.
 * @param {(snack: {call: boolean, type: string, message: string}) => void} props.setSnack Shows the result.
 * @returns {JSX.Element|null}
 */
export function CancelMoveOutButton({ orderDetail, setSnack }) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [errorDetail, setErrorDetail] = useState("");

  if (!canCancelMoveOut(orderDetail)) return null;

  const moveOutDate = moment(orderDetail.return_date).format("DD-MM-yyyy");

  /** Close the dialog unless a request is in flight. */
  const close = () => {
    if (busy) return;
    setOpen(false);
    setError("");
    setErrorDetail("");
  };

  /**
   * Cancel the move-out, then reload the order and its timeline.
   *
   * @returns {void}
   */
  const handleConfirm = () => {
    if (busy) return;
    setBusy(true);
    setError("");
    setErrorDetail("");
    setSnack({ call: false, type: "", message: "" });
    cancelContainerMoveOut(orderDetail.id)
      .then((res) => {
        setOpen(false);
        setSnack({
          call: true,
          type: "success",
          message: res?.data?.message || "The move-out has been cancelled.",
        });
        dispatch(fetchMyOrderDetailCard(orderDetail.id));
        dispatch(fetchMyOrderTimeline(orderDetail.id));
      })
      .catch((err) => {
        setError(
          err?.response?.data?.message ||
            "The move-out could not be cancelled. Please try again."
        );
        setErrorDetail(refusalDetail(err));
      })
      .finally(() => setBusy(false));
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-light font-size-sm font-weight-bolder text-danger btn-transparent"
        onClick={() => setOpen(true)}
      >
        Cancel move-out
      </button>
      <Modal show={open} onHide={close} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cancel move-out</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This cancels the move-out booked for <strong>{moveOutDate}</strong>.
          </p>
          <p className="mb-0">
            The order carries on, the customer keeps their access, and their
            payments continue from the date they are paid up to. The customer
            is emailed.
          </p>
          {error && (
            <div role="alert" className="text-danger mt-3">
              {error}
              {/* Rendered as text, never as HTML: it is Stripe's own wording. */}
              {errorDetail && <div className="mt-1">{errorDetail}</div>}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light"
            onClick={close}
            disabled={busy}
          >
            Keep the move-out
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleConfirm}
            disabled={busy}
          >
            {busy ? "Cancelling..." : "Cancel move-out"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
